import { useLazyQuery } from '@apollo/client';
import { DayOfWeek, Label, Stack, Text } from '@fluentui/react';
import clsx from 'clsx';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { FormHookDateSelect } from 'common/components/FormHooksFields/FormHookDateSelect';
import { FormHookTimePicker } from 'common/components/FormHooksFields/FormHookTimePicker';
import { useCommonStyles } from 'common/styles';
import { dateConvertions, dateFormat } from 'common/utils/dateFormats';
import { EndDateCalculator } from 'purchaseOrder/view/FormView/POItems/POItemsModal/POItemsForm/EndDateCalculator';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';
import { TripCreateFormValues } from '../../../../types';
import { AddressSectionInput } from '../components/AddressSectionInput';
import { ContactSectionInput } from '../components/ContactSectionInput';
import { ProviderSearch } from '../components/ProviderSearch';
import { RateAndTotalSection } from '../components/RateAndTotalSection';
import {
  DateCalcConsecutiveNightTotals,
  DateCalcConsecutiveNightTotalsVariables,
} from '../components/RateAndTotalSection/__generated__/DateCalcConsecutiveNightTotals';
import CAL_QUERY from '../components/RateAndTotalSection/DateCalcConsecutiveNightTotals.graphql';

export const VehicleRental: React.FC<{
  tripIndex: number;
  tripSectionId: string | null | undefined;
  travelAuthorizationData:
    | Partial<TravelAuthorization_travelAuthorization>
    | null
    | undefined;
  listRefreshRequest: () => void;
}> = ({
  tripIndex,
  tripSectionId,
  travelAuthorizationData,
  listRefreshRequest,
}) => {
  const baseField = `tripItems.${tripIndex}.`;
  const commonStyles = useCommonStyles();
  const { setValue } = useFormContext<TripCreateFormValues>();
  const rentalPrice = useWatch({ name: `${baseField}rentalPrice` });
  const checkInDate = useWatch({ name: `${baseField}checkInDate` });
  const checkOutDate = useWatch({ name: `${baseField}checkOutDate` });
  const itemTotal = useWatch({ name: `${baseField}itemTotal` });
  const [getItemTotals, { loading }] = useLazyQuery<
    DateCalcConsecutiveNightTotals,
    DateCalcConsecutiveNightTotalsVariables
  >(CAL_QUERY, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted(data) {
      setValue(
        `tripItems.${tripIndex!}.itemTotal`,
        data.dateCalcConsecutiveNightTotals?.periodTotal!,
        { shouldTouch: true }
      );
      setValue(
        `tripItems.${tripIndex!}.rentalTerm`,
        data.dateCalcConsecutiveNightTotals?.fullPeriods!,
        { shouldTouch: true }
      );
    },
  });

  const calculateNewRate = (
    rate: string,
    startDate: string,
    endDate: string
  ) => {
    if (parseInt(rate) > 0 && startDate && endDate) {
      getItemTotals({
        variables: {
          endDate,
          startDate,
          rate,
        },
      });
    }
  };

  const { _isUpdatable, _isNonAmountUpdatableAfterApproval } = {
    ...travelAuthorizationData,
  };

  const isDisabled = !!_isNonAmountUpdatableAfterApproval
    ? false
    : !_isUpdatable;

  useEffect(() => {
    listRefreshRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack tokens={{ childrenGap: 40 }}>
      <Stack horizontal horizontalAlign="end">
        <Stack
          horizontal
          tokens={{ childrenGap: 20, padding: '0px 20px 0px 0px' }}
        >
          <Text variant="xLarge">Total </Text>
          <AmountTextView
            className={clsx(commonStyles.bold, commonStyles.colorThemePrimary)}
            value={itemTotal?.toString() || '0.00'}
            variant="xLarge"
          />
        </Stack>
      </Stack>
      <Stack horizontalAlign="space-between" horizontal verticalAlign="center">
        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
          <LabelWrapper content="PickUp" disabled={isDisabled}>
            <FormHookDateSelect
              name={`${baseField}checkInDate`}
              placeholder="Pick-up Day"
              firstDayOfWeek={DayOfWeek.Sunday}
              firstWeekOfYear={1}
              showMonthPickerAsOverlay
              showGoToToday
              styles={{ root: { width: 150 } }}
              onSelectDateCallback={(date) => {
                calculateNewRate(
                  rentalPrice,
                  dateFormat(date!.toString()),
                  checkOutDate
                );
              }}
              disabled={isDisabled}
            />
          </LabelWrapper>
          <Stack style={{ marginTop: -25 }}>
            <EndDateCalculator
              calculationType={'PO_DAILY_NIGHTLY'}
              startDate={checkInDate}
              label="Calculate Drop off Date"
              disabled={isDisabled}
              onEndDateReturn={(date) => {
                setValue(
                  `tripItems.${tripIndex}.checkOutDate`,
                  dateConvertions(date),
                  { shouldDirty: true }
                );
              }}
            />
          </Stack>
          <LabelWrapper content="Time" disabled={isDisabled}>
            <FormHookTimePicker
              name={`${baseField}checkInTime`}
              disabled={isDisabled}
            />
          </LabelWrapper>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
          <LabelWrapper content="Drop off" disabled={isDisabled}>
            <FormHookDateSelect
              name={`${baseField}checkOutDate`}
              placeholder="Drop-off Day"
              firstDayOfWeek={DayOfWeek.Sunday}
              firstWeekOfYear={1}
              showMonthPickerAsOverlay
              showGoToToday
              disabled={isDisabled}
              styles={{ root: { width: 150 } }}
              onSelectDateCallback={(date) => {
                calculateNewRate(
                  rentalPrice,
                  checkInDate,
                  dateFormat(date!.toString())
                );
              }}
            />
          </LabelWrapper>

          <LabelWrapper content="Time" disabled={isDisabled}>
            <FormHookTimePicker
              name={`${baseField}checkOutTime`}
              disabled={isDisabled}
            />
          </LabelWrapper>
        </Stack>
      </Stack>

      <ProviderSearch
        label="Rental Company"
        placeholder="Enter rental company"
        tripIndex={tripIndex}
        name={`${baseField}rentalName`}
        tripSectionId={tripSectionId}
        disabled={isDisabled}
      />

      <Stack
        horizontal
        horizontalAlign="space-between"
        tokens={{ childrenGap: 100 }}
      >
        <AddressSectionInput baseField={baseField} disabled={isDisabled} />
        <ContactSectionInput baseField={baseField} disabled={isDisabled} />
      </Stack>

      <RateAndTotalSection
        baseField={baseField}
        tripIndex={tripIndex}
        loading={loading}
        onRateEntry={(rate) => {
          calculateNewRate(rate, checkInDate, checkOutDate);
        }}
        disabled={!_isUpdatable}
      />
    </Stack>
  );
};

export const LabelWrapper: React.FC<{
  content: string;
  children: React.ReactNode;
  grow?: boolean | number | 'inherit' | 'initial' | 'unset';
  disabled?: boolean;
}> = ({ children, content, grow, disabled }) => {
  return (
    <Stack tokens={{ childrenGap: 10 }} horizontal grow={grow}>
      <Label disabled={disabled}>{content}</Label>
      {children}
    </Stack>
  );
};
